import { ToggleGroupCard, ToggleGroupCardItem } from '@/components/ui/toggle-group';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectLabel,
} from '@/components/ui/select';
import {
  ActivityNotificationEnum,
  DisruptionNotificationEnum,
  DisruptionNotificationGracePeriodEnum,
} from 'src/graphql/schema';
import { cn } from 'src/lib/utils';
import { gracePeriodOptions, disruptedNotificationsOptionsRich } from 'src/shared/entities/escrow-notification-options';
import { getEscrowActivityNotificationOptions } from 'src/shared/entities/escrow-activity-notification-options';

const { activityNotificationOptionsRich } = getEscrowActivityNotificationOptions('software');

type Props = {
  settings: {
    activityNotification: ActivityNotificationEnum;
    disruptionNotification: DisruptionNotificationEnum;
    disruptionNotificationGracePeriod?: DisruptionNotificationGracePeriodEnum | null;
  };
  onActivityNotificationChange: (value: ActivityNotificationEnum) => void;
  onDisruptionNotificationChange: (value: DisruptionNotificationEnum) => void;
  onGracePeriodChange: (value: DisruptionNotificationGracePeriodEnum) => void;
  className?: string;
};
const PartyNotificationSettings = ({
  settings,
  onActivityNotificationChange,
  onDisruptionNotificationChange,
  onGracePeriodChange,
  className,
}: Props) => {
  const { activityNotification, disruptionNotification, disruptionNotificationGracePeriod } = settings;

  const handleDisruptionNotificationChange = (value: DisruptionNotificationEnum) => {
    if (!value) return;
    onDisruptionNotificationChange(value);
  };

  return (
    <div className={cn('grid grid-cols-1 gap-10 md:grid-cols-2', className)}>
      <Card>
        <CardHeader>
          <CardTitle>Activity notifications</CardTitle>
          <CardDescription>
            What type of activity notifications would you like to receive?
            <br />
            These may include details like file names and deposit timestamps.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ToggleGroupCard
            type="single"
            className="flex-col w-full"
            onValueChange={onActivityNotificationChange}
            value={activityNotification}
          >
            {activityNotificationOptionsRich.map(({ value, icon: Icon, label, description }) => (
              <ToggleGroupCardItem key={value} value={value} icon={Icon} title={label} description={description} />
            ))}
          </ToggleGroupCard>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Disrupted access notifications</CardTitle>
          <CardDescription>
            Choose whether to alert members if an asset becomes inaccessible or can no longer be deposited.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <ToggleGroupCard
            type="single"
            className="flex-col w-full"
            onValueChange={handleDisruptionNotificationChange}
            value={disruptionNotification}
          >
            {disruptedNotificationsOptionsRich.map(({ value, icon: Icon, label }) => (
              <ToggleGroupCardItem key={value} value={value} icon={Icon} title={label} />
            ))}
          </ToggleGroupCard>

          {disruptionNotification === DisruptionNotificationEnum.SendNotification && (
            <div className="space-y-2">
              <div className="text-sm font-semibold text-foreground">Disruption grace period</div>
              <Select value={disruptionNotificationGracePeriod ?? ''} onValueChange={onGracePeriodChange}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select grace period" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>Disruption grace period</SelectLabel>
                    {gracePeriodOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                We’ll keep retrying the deposit during this period before notifying you that something’s wrong.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PartyNotificationSettings;
